import styles from './Category.module.css';
import { CATEGORY_DESCRIPTION } from '../../../../config/category-config';
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useProductsHook, useLanguageHook, useRetailerHook } from "../../../../Hooks/use-context-hooks";
import {
  ListProductsByCategorySubcategory,
  NormalizeCategoryId,
  FilterProductsByCategoryId,
  sortProductsByMsrpAndTitle,
  CATEGORY_SUBCATEGORY_ORDER,

} from "../../../../Utils/category-helper";
import { NormalizeSlugs } from "../../../../Utils/helper-functions";
import { useRoutingHook } from "../../../../Hooks/use-context-hooks";
import ScrollableRow from "../../../Components/ProductElements/Containers/ScrollableRow/ScrollableRow";
import HeroContainer from '../../../Components/UI/HeroContainer/HeroContainer';
import rangeImage from '../../../Theme/Product/SubcategoryImages/cooking-1.webp';
import dishImage from '../../../Theme/Product/SubcategoryImages/dish-4.webp';
import laundryImage from '../../../Theme/Product/SubcategoryImages/laundry-16.webp';
import lgStudioImage from '../../../Theme/Product/SubcategoryImages/lgstudio-kitchen-3.webp';
import fridgeImage from '../../../Theme/Product/SubcategoryImages/fridge-12.webp';
import { GridSystem } from '../../../Components/GridSystem/GridSystem';
import SectionTextContainer from '../../../Components/UI/SectionTextContainer/SectionTextContainer';
import { ExternalLinkButton } from "../../../../Utils/link-helper";
import ScrollableBodyContainer from "../../../Components/UI/BodyContainer/ScrollableBodyContainer";
import { filterBrowsableProducts } from '../../../../Utils/product-availability';

const CATEGORY_NAMES_FR = {
  "Laundry": "Lessive",
  "Refrigeration": "Réfrigérateur",
  "Cooking": "Cuisson",
  "Dishwasher": "Lave-vaisselle",
  "LG Studio": "LG Studio"
};

const CategoryPage = () => {
  const { isHomeDepotApp } = useRetailerHook();
  const { translatedText, language } = useLanguageHook();
  const { publicProducts } = useProductsHook();
  const { productNavigationState, setProductNavigationState } = useRoutingHook();

  const { categoryId } = useParams();
  const normalizedCategoryId = NormalizeCategoryId(categoryId);

  // The grouping must use the correct subcategory property for the language

  // const reducedProducts2 = ListProductsByCategorySubcategory(
  //   FilterProductsByCategoryId(publicProducts, normalizedCategoryId),
  //   language
  // );

  const categoryProducts = FilterProductsByCategoryId(publicProducts, normalizedCategoryId);

  const browsableCategoryProducts = filterBrowsableProducts(categoryProducts);

  const reducedProducts = ListProductsByCategorySubcategory(
    browsableCategoryProducts,
    language
  );


    // console.log('reduced products ',reducedProducts)
    // console.log('reduced products 2',reducedProducts2)


  // --- KEY CHANGE: Use the correct category key for EN/FR ---
  // If the URL always passes English (e.g., /categories/Laundry), map it to FR for lookup.
  const langCategoryKey = language === "FR"
    ? CATEGORY_NAMES_FR[normalizedCategoryId] || normalizedCategoryId
    : normalizedCategoryId;

  // Use the correct subcategory order for this language/category.
  const subcategoryOrder =
    CATEGORY_SUBCATEGORY_ORDER[language === "FR" ? "FR" : "EN"][langCategoryKey] || [];

  // Build the ordered subcategory product arrays.
  const renderedSubcategoryProducts = subcategoryOrder
    .filter(subcatName => reducedProducts[subcatName]) // only include ones that exist
    .map(subcatName => ({
      subcategory: subcatName,
      products: sortProductsByMsrpAndTitle({ [subcatName]: reducedProducts[subcatName] })[subcatName]
    }));

  // Sync nav context if changed (keep this as before)
  useEffect(() => {
    const subcategoryProductLists = {};
    renderedSubcategoryProducts.forEach(({ subcategory, products }) => {
      subcategoryProductLists[NormalizeSlugs(subcategory)] = products;
    });
    const current = productNavigationState?.subcategoryProductLists || {};
    const newKeys = Object.keys(subcategoryProductLists);
    const currentKeys = Object.keys(current);
    const hasChanged =
      newKeys.length !== currentKeys.length ||
      newKeys.some(
        key =>
          !current[key] ||
          current[key].length !== subcategoryProductLists[key].length ||
          current[key].some((p, i) => p.title !== subcategoryProductLists[key][i]?.title)
      );
    if (hasChanged) {
      setProductNavigationState(prevState => ({
        ...prevState,
        subcategoryProductLists
      }));
    }
    // eslint-disable-next-line
  }, [renderedSubcategoryProducts, setProductNavigationState, language]);


  // ========================================
  //  CATEGORY BACK IMG LOGIC
  // ========================================
  const categoryBackgroundImages = {
    "Cuisson": rangeImage,
    "Cooking": rangeImage,
    "Dishwasher": dishImage,
    "Lave-vaisselle": dishImage,
    "LG Studio": lgStudioImage,
    // "LG Studio": lgStudioImage,
    "Laundry": laundryImage,
    "Lessive": laundryImage,
    "Refrigeration": fridgeImage,
    "Réfrigérateur": fridgeImage,
  };
  const categoryNamesInEnglish = {
    "Cuisson": "Cooking",
    "Lave-vaisselle": "Dishwasher",
    "Lessive": "Laundry",
    "Réfrigérateur": "Refrigeration",
    "LG Studio": "LG Studio"
  };
  const categoryBackgroundPositions = {
    "Cooking": 'top',
    "Dishwasher": 'bottom',
    "LG Studio": 'center',
    "Laundry": 'center',
    "Refrigeration": 'top',
  };
  const getBackgroundPosition = (category) => {
    return categoryBackgroundPositions[category] || 'center';
  }
  const getEnglishCategoryName = (category) => {
    return categoryNamesInEnglish[category] || category;
  };

  const backgroundImage = categoryBackgroundImages[getEnglishCategoryName(categoryId)];
  // ========================================
  //  CATEGORY TEXT LOGIC
  // ========================================

  const getCategoryDescription = (category, lang) => {
    const englishCategory = getEnglishCategoryName(category);
    return lang === "EN"
      ? CATEGORY_DESCRIPTION.EN[englishCategory]
      : CATEGORY_DESCRIPTION.FR[englishCategory];
  };


  const translateCategoryName = (category) => {
    if (language === "FR") {
      return Object.keys(categoryNamesInEnglish).find(
        (key) => categoryNamesInEnglish[key] === category
      ) || category;
    } else {
      return category;
    }
  };

  // STEP UP CHARTS
  const stepUpChartLinkMapLG = {
    Cooking: {
      EN: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Step-Up+Charts/English/LG+Cooking+Step+Up+Chart+En.pdf",
      FR: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Step-Up+Charts/French/LG+Cooking+Step+Up+Chart+Fr.pdf",
    },
    Laundry: {
      EN: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Step-Up+Charts/English/LG+Laundry+Step+Up+Chart.pdf",
      FR: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Step-Up+Charts/French/LG+Laundry+Step+Up+Chart+Fr.pdf",
    },
    Dishwasher: {
      EN: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Step-Up+Charts/English/LG+Dish+Step+Up+Chart+En.pdf",
      FR: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Step-Up+Charts/French/LG+Dish+Step+Up+Chart+Fr.pdf",
    },
    Refrigeration: {
      EN: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Step-Up+Charts/English/LG+Fridge+Step+Up+Chart+En.pdf",
      FR: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Step-Up+Charts/French/LG+Fridge+Step+Up+Chart+Fr.pdf",
    }
  };

  const stepUpChartLinkMapHD = {
    Cooking: {
      EN: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Retail+Link/Step+Up+Charts/English/LG+Cooking+Step+Up+Chart+HD+En.pdf",
      FR: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Retail+Link/Step+Up+Charts/French/LG+Cooking+Step+Up+Chart+HD+Fr.pdf",
    },
    Laundry: {
      EN: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Retail+Link/Step+Up+Charts/English/LG+HD+Laundry+Step+Up+Chart+En.pdf",
      FR: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Retail+Link/Step+Up+Charts/French/LG+HD+Laundry+Step+Up+Chart+Fr.pdf",
    },
    Dishwasher: {
      EN: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Retail+Link/Step+Up+Charts/English/LG+HD+Dish+Step+Up+Chart+En.pdf",
      FR: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Retail+Link/Step+Up+Charts/French/LG+HD+Dish+Step+Up+Chart+Fr.pdf",
    },
    Refrigeration: {
      EN: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Retail+Link/Step+Up+Charts/English/LG+HD+Fridge+Step+Up+En.pdf",
      FR: "https://ha-product-guide.s3.us-east-2.amazonaws.com/Product+Guide+Files/Retail+Link/Step+Up+Charts/French/LG+HD+Fridge+Step+Up+Chart+Fr.pdf",
    }
  };

  const renderRetailerStepUpChartLinks = (category) => {
    const linkMap = isHomeDepotApp.isHomeDepotActive ? stepUpChartLinkMapHD : stepUpChartLinkMapLG;
    const categoryLinkData = linkMap[category];

    if (!categoryLinkData) return null; // Handle missing category
    return categoryLinkData[language]; // Return the correct language-specific link
  };




  return (
    <div>
      <HeroContainer
        backgroundImage={backgroundImage}
        backgroundPosition={getBackgroundPosition(getEnglishCategoryName(categoryId))}
        title={translateCategoryName(categoryId)}
      />

      <GridSystem
        containerPaddingBottom="2rem"
      >
        <div className={styles.introductionSectionWrapper}>
          <SectionTextContainer
            buttonElement={
              <div className={styles.buttonBox2}>
                <ExternalLinkButton href={renderRetailerStepUpChartLinks(categoryId)} linkText={`${translatedText.viewStepUpChart} `} />
              </div>
            }
            studioExclusion={categoryId}
            titleText={`${translatedText.explore} ${translateCategoryName(categoryId)}`}
            descriptionText={getCategoryDescription(categoryId, language)}
          >
          </SectionTextContainer>
        </div>

      </GridSystem>
      <GridSystem containerBackgroundColor="var(--color-gray-06)">
        <ScrollableBodyContainer>
          <section className={styles.scrollContainerWrapper}>
            {renderedSubcategoryProducts.map((subcategoryProduct, idx) => (
              <div key={idx}>
                <section id={subcategoryProduct.subcategory} className={styles.categorySectionWrapper} >
                  <div className={styles.categorySectionHeader}>
                    {subcategoryProduct.subcategory.length > 0 ? (
                      <div className={styles.categoryTextGroup}>
                        <div className={styles.resultsCountBox}>
                          <span className={styles.catCountNum}>
                            {subcategoryProduct.products.length}
                          </span>
                          <p className={styles.catCount}>{translatedText.results}</p>
                        </div>
                        <div className={styles.sectionTitleContainer}>
                          <p className={styles.sectionTitle}>
                            {subcategoryProduct.subcategory}
                          </p>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className={styles.allCategoriesProductsContainer}>
                    <ScrollableRow products={subcategoryProduct.products} />
                  </div>
                </section>
              </div>
            ))}
          </section>
        </ScrollableBodyContainer>
      </GridSystem>
    </div>
  );
};

export default CategoryPage;
